import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  Zap,
  Clock,
  DollarSign,
  AlertTriangle,
  Wifi,
  WifiOff,
  Trash2,
  IndianRupee,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const PROVIDER_COLORS = {
  openai: "#10A37F",
  anthropic: "#F97316",
  google: "#1D4ED8",
  groq: "#4338CA",
};
const PROVIDER_NAMES = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  groq: "Groq",
};
const PROVIDER_ICONS = { openai: "O", anthropic: "A", google: "G", groq: "Q" };
const USD_TO_INR = 85.5;
const ANOMALY_COST_THRESHOLD = 0.05;
const ANOMALY_LATENCY_THRESHOLD = 15;

function formatCost(usd) {
  if (usd === 0) return "$0.00";
  if (usd < 0.001) return `$${usd.toFixed(6)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(4)}`;
}

function formatTokens(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 5) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function RateGauge({ label, value, unit, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div data-testid={`rate-gauge-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-sm font-semibold tabular-nums">
          {typeof value === "number" ? (value < 1 && value > 0 ? value.toFixed(4) : Math.round(value)) : value}
          <span className="text-xs text-muted-foreground ml-0.5">{unit}</span>
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color || "hsl(173, 80%, 40%)" }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function EventRow({ event, isNew }) {
  const isAnomaly =
    event.estimated_cost > ANOMALY_COST_THRESHOLD ||
    event.latency > ANOMALY_LATENCY_THRESHOLD;
  const providerColor = PROVIDER_COLORS[event.provider] || "#6B7280";

  return (
    <motion.div
      data-testid="monitor-event-row"
      initial={isNew ? { opacity: 0, x: -20, scale: 0.97 } : false}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`px-3 py-2.5 rounded-lg border transition-colors ${
        isAnomaly
          ? "border-amber-300/60 bg-amber-50/50"
          : "border-border/40 bg-card/50 hover:bg-muted/30"
      }`}
    >
      <div className="flex items-center gap-2.5">
        {/* Provider badge */}
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: providerColor }}
        >
          {PROVIDER_ICONS[event.provider] || "?"}
        </div>

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-medium truncate">
              {event.model}
            </span>
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 h-4 shrink-0"
              style={{ borderColor: providerColor, color: providerColor }}
            >
              {event.task_type}
            </Badge>
            {event.origin && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                {event.origin}
              </Badge>
            )}
            {isAnomaly && (
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
            <span className="tabular-nums">
              {formatTokens(event.usage?.total_tokens || 0)} tokens
            </span>
            <span className="tabular-nums">{event.latency}s</span>
            <span className="tabular-nums">{timeAgo(event.timestamp)}</span>
          </div>
        </div>

        {/* Cost */}
        <div className="text-right shrink-0">
          <div className={`text-[13px] font-semibold tabular-nums ${isAnomaly ? "text-amber-600" : ""}`}>
            {formatCost(event.estimated_cost)}
          </div>
          <div className="text-[10px] text-muted-foreground tabular-nums">
            {"\u20B9"}{(event.estimated_cost * USD_TO_INR).toFixed(2)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function LiveMonitorPage({ api }) {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [rateStats, setRateStats] = useState({
    requests_per_min: 0,
    tokens_per_min: 0,
    cost_per_min: 0,
  });
  const [sessionTotals, setSessionTotals] = useState({
    requests: 0,
    tokens: 0,
    cost: 0,
  });
  const [autoscroll, setAutoscroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const statsInterval = useRef(null);
  const feedRef = useRef(null);
  const newEventIds = useRef(new Set());

  const wsUrl = api
    .replace("https://", "wss://")
    .replace("http://", "ws://")
    + "/monitor/ws";

  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };

    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data);
        if (data.type === "init") {
          setEvents(data.recent || []);
          setRateStats(data.rate_stats || rateStats);
        } else if (data.type === "llm_call" && !paused) {
          newEventIds.current.add(data.id);
          setTimeout(() => newEventIds.current.delete(data.id), 2000);
          setEvents((prev) => [data, ...prev].slice(0, 200));
          setSessionTotals((prev) => ({
            requests: prev.requests + 1,
            tokens: prev.tokens + (data.usage?.total_tokens || 0),
            cost: prev.cost + (data.estimated_cost || 0),
          }));
        } else if (data.type === "rate_stats") {
          setRateStats(data.rate_stats);
        } else if (data.type === "pong") {
          // heartbeat ok
        }
      } catch { /* ignore parse errors */ }
    };

    ws.onclose = () => {
      setConnected(false);
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [wsUrl, paused]);

  useEffect(() => {
    connectWs();
    // Request rate stats every 5s
    statsInterval.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send("stats");
      }
    }, 5000);
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (statsInterval.current) clearInterval(statsInterval.current);
    };
  }, [connectWs]);

  const clearEvents = () => {
    setEvents([]);
    setSessionTotals({ requests: 0, tokens: 0, cost: 0 });
  };

  // Provider breakdown from current events
  const providerBreakdown = {};
  for (const e of events) {
    const p = e.provider;
    if (!p) continue;
    if (!providerBreakdown[p]) providerBreakdown[p] = { requests: 0, tokens: 0, cost: 0 };
    providerBreakdown[p].requests++;
    providerBreakdown[p].tokens += e.usage?.total_tokens || 0;
    providerBreakdown[p].cost += e.estimated_cost || 0;
  }

  const anomalyCount = events.filter(
    (e) =>
      e.estimated_cost > ANOMALY_COST_THRESHOLD ||
      e.latency > ANOMALY_LATENCY_THRESHOLD
  ).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="live-monitor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <Activity className="w-7 h-7 text-primary" />
            Live Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time API usage tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="autoscroll"
              checked={!paused}
              onCheckedChange={(v) => setPaused(!v)}
              data-testid="monitor-pause-toggle"
            />
            <Label htmlFor="autoscroll" className="text-xs text-muted-foreground">
              {paused ? "Paused" : "Live"}
            </Label>
          </div>
          <Badge
            data-testid="monitor-connection-status"
            variant={connected ? "default" : "destructive"}
            className="flex items-center gap-1"
          >
            {connected ? (
              <><Wifi className="w-3 h-3" /> Connected</>
            ) : (
              <><WifiOff className="w-3 h-3" /> Reconnecting</>
            )}
          </Badge>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Card data-testid="stat-session-requests">
          <CardContent className="p-4">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Session Requests</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{sessionTotals.requests}</div>
          </CardContent>
        </Card>
        <Card data-testid="stat-session-tokens">
          <CardContent className="p-4">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Session Tokens</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{formatTokens(sessionTotals.tokens)}</div>
          </CardContent>
        </Card>
        <Card data-testid="stat-session-cost">
          <CardContent className="p-4">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Session Cost</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{formatCost(sessionTotals.cost)}</div>
            <div className="text-[10px] text-muted-foreground tabular-nums">
              {"\u20B9"}{(sessionTotals.cost * USD_TO_INR).toFixed(2)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-rate">
          <CardContent className="p-4">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Rate /min</div>
            <div className="text-2xl font-bold mt-1 tabular-nums">{rateStats.requests_per_min}</div>
            <div className="text-[10px] text-muted-foreground tabular-nums">
              {formatTokens(rateStats.tokens_per_min)} tok
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-anomalies">
          <CardContent className="p-4">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Anomalies
            </div>
            <div className={`text-2xl font-bold mt-1 tabular-nums ${anomalyCount > 0 ? "text-amber-600" : ""}`}>
              {anomalyCount}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {">"}{formatCost(ANOMALY_COST_THRESHOLD)} or {">"}{ANOMALY_LATENCY_THRESHOLD}s
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Rate Gauges + Provider Breakdown */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-primary" /> Rate Gauges
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <RateGauge
                label="Requests"
                value={rateStats.requests_per_min}
                unit="/min"
                max={60}
                color="hsl(173, 80%, 40%)"
              />
              <RateGauge
                label="Tokens"
                value={rateStats.tokens_per_min}
                unit="/min"
                max={100000}
                color="hsl(217, 91%, 60%)"
              />
              <RateGauge
                label="Cost"
                value={rateStats.cost_per_min}
                unit="$/min"
                max={1}
                color="hsl(25, 95%, 53%)"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <IndianRupee className="w-4 h-4 text-primary" /> By Provider
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {Object.keys(PROVIDER_NAMES).map((p) => {
                const data = providerBreakdown[p];
                return (
                  <div key={p} className="flex items-center justify-between py-1.5" data-testid={`provider-breakdown-${p}`}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: PROVIDER_COLORS[p] }}
                      />
                      <span className="text-xs font-medium">{PROVIDER_NAMES[p]}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold tabular-nums">
                        {data ? formatCost(data.cost) : "$0.00"}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-1.5 tabular-nums">
                        {data ? data.requests : 0} req
                      </span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Live Event Feed */}
        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-2 pt-4 px-4 flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Activity className="w-4 h-4" />
                Live Feed
                {events.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] ml-1">
                    {events.length}
                  </Badge>
                )}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={clearEvents}
                data-testid="clear-events-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Clear
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ScrollArea className="h-[520px]" ref={feedRef}>
                {events.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center" data-testid="monitor-empty-state">
                    <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mb-3">
                      <Activity className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground">
                      No events yet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      API calls will appear here in real-time
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <AnimatePresence initial={false}>
                      {events.map((event) => (
                        <EventRow
                          key={event.id || event.timestamp}
                          event={event}
                          isNew={newEventIds.current.has(event.id)}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
